import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAdjustInventory } from '../../../api/mutations/inventory.mutations';
import { useBranchStore } from '../../../stores/branch.store';

// Inventory item from API response
interface InventoryItemFromAPI {
  _id: string;
  ingredientId: {
    _id: string;
    name: string;
    sku: string;
    category: string;
    unit: string;
    minStockLevel?: number;
  };
  branchId: string;
  quantity: number;
  unit: string;
}

interface AdjustModalProps {
  item: InventoryItemFromAPI;
  onClose: () => void;
}

function AdjustModal({ item, onClose }: AdjustModalProps) {
  const { t } = useTranslation();
  const selectedBranchId = useBranchStore((state) => state.selectedBranchId);
  const adjustMutation = useAdjustInventory(selectedBranchId || '');

  const [adjustmentType, setAdjustmentType] = useState<'add' | 'subtract'>('add');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  // Extract values from the API structure
  const ingredientId = item.ingredientId?._id || '';
  const ingredientName = item.ingredientId?.name || 'Unknown';
  const currentStock = item.quantity || 0;
  const unit = item.unit || item.ingredientId?.unit || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const adjustmentQuantity = adjustmentType === 'add'
      ? parseFloat(quantity)
      : -parseFloat(quantity);

    try {
      await adjustMutation.mutateAsync({
        ingredientId,
        quantity: adjustmentQuantity,
        reason,
        notes: notes || undefined,
      });
      onClose();
    } catch (error) {
      console.error('Failed to adjust inventory:', error);
    }
  };

  const newStock = adjustmentType === 'add'
    ? currentStock + parseFloat(quantity || '0')
    : currentStock - parseFloat(quantity || '0');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-md rounded-lg bg-card">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">{t('inventory.adjustStock')}</h2>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-secondary tap-highlight-none"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Ingredient info */}
          <div className="rounded-lg bg-muted p-3">
            <div className="font-medium">{ingredientName}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {t('inventory.currentStock') || 'Current Stock'}: <span className="font-bold">{currentStock.toLocaleString()} {unit}</span>
            </div>
          </div>

          {/* Adjustment type */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('inventory.adjustmentType')}</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAdjustmentType('add')}
                className={`rounded-lg border py-3 font-medium transition-colors ${
                  adjustmentType === 'add'
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-background hover:bg-secondary'
                }`}
              >
                {t('inventory.add') || 'Add Stock'}
              </button>
              <button
                type="button"
                onClick={() => setAdjustmentType('subtract')}
                className={`rounded-lg border py-3 font-medium transition-colors ${
                  adjustmentType === 'subtract'
                    ? 'bg-red-500 text-white border-red-500'
                    : 'bg-background hover:bg-secondary'
                }`}
              >
                {t('inventory.subtract') || 'Remove Stock'}
              </button>
            </div>
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('inventory.quantity')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                step="0.01"
                min="0"
                required
                className="flex-1 rounded-lg border bg-background px-3 py-2"
                placeholder="0.00"
              />
              <span className="text-muted-foreground">{unit}</span>
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('inventory.reason')}</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              className="w-full rounded-lg border bg-background px-3 py-2"
            >
              <option value="">{t('inventory.selectReason') || 'Select reason...'}</option>
              <option value="correction">{t('inventory.correction') || 'Stock Correction'}</option>
              <option value="damaged">{t('inventory.damaged') || 'Damaged'}</option>
              <option value="expired">{t('inventory.expired') || 'Expired'}</option>
              <option value="found">{t('inventory.found') || 'Found Stock'}</option>
              <option value="other">{t('inventory.other') || 'Other'}</option>
            </select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('inventory.notes')}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 min-h-[80px]"
              placeholder={t('inventory.notesPlaceholder') || 'Additional notes...'}
            />
          </div>

          {/* Preview */}
          {quantity && (
            <div className="rounded-lg bg-secondary p-3">
              <div className="text-sm text-muted-foreground">{t('inventory.newStock') || 'New Stock Level'}</div>
              <div className="text-2xl font-bold mt-1">
                {newStock.toLocaleString()} <span className="text-lg text-muted-foreground">{unit}</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border bg-background px-4 py-2 hover:bg-secondary transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={adjustMutation.isPending || !quantity || !reason}
              className="flex-1 rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {adjustMutation.isPending ? t('common.loading') : t('common.confirm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdjustModal;
